import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import Button from '../ui/Button';
import { elearningAPI } from '../../services/elearningApi';
import { Enrollment, EnrollmentStatus, Module } from '../../types/elearning';
import { clearResume, readResume } from '../../utils/elearningResume';
import Spinner from '../ui/Spinner';

interface EnrollmentButtonProps {
  courseId: string;
  courseSlug: string;
  modules?: Module[];
  enrollment?: Enrollment;
  startLocked?: boolean;
  onEnrollmentChange?: (enrollment: Enrollment | null) => void;
}

function lessonHref(courseSlug: string, courseId: string, modules: Module[], enrollment: Enrollment) {
  const ordered = [...modules].sort((a, b) => a.order - b.order);
  if (ordered.length === 0) return null;

  const saved = readResume(enrollment.userId, courseId);
  const savedModule = saved ? ordered.find((module) => module.slug === saved.moduleSlug) : undefined;
  const completed = new Set(enrollment.moduleCompletions?.map((completion) => completion.moduleId));
  const target = savedModule || ordered.find((module) => !completed.has(module.id)) || ordered[0];
  const slideIndex = savedModule && saved ? saved.slideIndex : 0;
  const slide = slideIndex > 0 ? `?slide=${slideIndex}` : '';
  return `/elearning/courses/${courseSlug}/modules/${target.slug}${slide}`;
}

const primaryLinkClass =
  'inline-flex items-center justify-center px-6 py-3 rounded-md font-medium transition-colors duration-150 focus-ring bg-primary text-white hover:bg-primary-dark';

export default function EnrollmentButton({
  courseId,
  courseSlug,
  modules = [],
  enrollment,
  startLocked = false,
  onEnrollmentChange,
}: EnrollmentButtonProps) {
  const { isSignedIn } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEnroll = async () => {
    if (!isSignedIn) {
      // Redirect to sign in or show modal
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const newEnrollment = await elearningAPI.enrollInCourse(courseId);
      onEnrollmentChange?.(newEnrollment);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enroll in course');
    } finally {
      setLoading(false);
    }
  };

  const handleUnenroll = async () => {
    if (!enrollment) return;

    try {
      setLoading(true);
      setError(null);
      await elearningAPI.unenrollFromCourse(courseId);
      if (enrollment.userId) clearResume(enrollment.userId, courseId);
      onEnrollmentChange?.(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unenroll from course');
    } finally {
      setLoading(false);
    }
  };

  if (!isSignedIn) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600">
          Sign in to enroll in this course
        </p>
        <Button variant="primary" disabled>
          Enroll in Course
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <Spinner size={20} />
        <span className="text-sm text-gray-600">Processing...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-red-600">{error}</p>
        <Button variant="primary" onClick={handleEnroll}>
          Try Again
        </Button>
      </div>
    );
  }

  if (startLocked && !enrollment) {
    return (
      <div className="space-y-2">
        <Button variant="primary" disabled>
          Start
        </Button>
        <p className="text-sm text-gray-600">
          Finish the prerequisite courses before starting this one.
        </p>
      </div>
    );
  }

  if (enrollment) {
    const href = lessonHref(courseSlug, courseId, modules, enrollment);
    const hasProgress =
      enrollment.progress > 0 ||
      (enrollment.moduleCompletions?.length ?? 0) > 0 ||
      readResume(enrollment.userId, courseId) !== null;

    if (enrollment.status === EnrollmentStatus.COMPLETED) {
      return (
        <div className="space-y-2">
          <p className="text-sm font-medium text-accent">Completed • {enrollment.progress}% complete</p>
          <p className="text-sm text-gray-600">
            You completed this course on{' '}
            {enrollment.completedAt
              ? new Date(enrollment.completedAt).toLocaleDateString()
              : 'N/A'}
          </p>
          <Button variant="secondary" onClick={handleUnenroll}>
            Unenroll from Course
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {startLocked ? (
          <Button variant="primary" disabled>
            Start
          </Button>
        ) : (
          href && (
            <Link to={href} className={primaryLinkClass}>
              {hasProgress ? 'Continue' : 'Start'}
            </Link>
          )
        )}
        <p className="text-sm font-medium text-accent">
          Enrolled • {enrollment.progress}% complete
        </p>
        <Button variant="secondary" onClick={handleUnenroll}>
          Unenroll from Course
        </Button>
      </div>
    );
  }

  return (
    <Button variant="primary" onClick={handleEnroll}>
      Enroll in Course
    </Button>
  );
}

